import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import PlatformUpdatesCarousel from '../components/PlatformUpdatesCarousel';
import OtpBoxInput from '../components/auth/OtpBoxInput';
import AuthSuccessOverlay from '../components/auth/AuthSuccessOverlay';

const STADIUM_IMAGE = 'https://images.unsplash.com/photo-1676746424139-77f8bd8922a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxzb2NjZXIlMjBzdGFkaXVtJTIwbGlnaHRzfGVufDB8fHx8MTc4ODA0NTM0MXww&ixlib=rb-4.1.0&q=85';

const DEVICE_TOKEN_KEY = 'tasyar_device_token';

const EnhancedProfessionalLogin = () => {
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({ platform_name: 'تسيار', platform_logo: null });
  const [hasUpdates, setHasUpdates] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);

  // خطوة OTP بعد صحة كلمة المرور
  const [step, setStep] = useState('form'); // 'form' | 'otp'
  const [otp, setOtp] = useState({ challengeId: '', maskedEmail: '', expiresIn: 300, resendAfter: 60 });
  const [otpCode, setOtpCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [expiresLeft, setExpiresLeft] = useState(300);
  const [resendLeft, setResendLeft] = useState(60);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successUserName, setSuccessUserName] = useState('');

  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // مسار "نسيت كلمة المرور" عبر OTP
  const [fpStep, setFpStep] = useState('identifier'); // 'identifier' | 'reset' | 'success'
  const [fpIdentifier, setFpIdentifier] = useState('');
  const [fpChallengeId, setFpChallengeId] = useState('');
  const [fpCode, setFpCode] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [fpMessage, setFpMessage] = useState('');
  const [fpError, setFpError] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpResendLoading, setFpResendLoading] = useState(false);
  const [fpResendLeft, setFpResendLeft] = useState(0);

  useEffect(() => {
    if (!showForgotModal || fpResendLeft <= 0) return;
    const t = setInterval(() => setFpResendLeft((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(t);
  }, [showForgotModal, fpResendLeft]);

  const resetForgotModalState = () => {
    setFpStep('identifier');
    setFpIdentifier('');
    setFpChallengeId('');
    setFpCode('');
    setFpNewPassword('');
    setFpConfirmPassword('');
    setFpMessage('');
    setFpError('');
    setFpResendLeft(0);
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    resetForgotModalState();
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    setFpLoading(true);
    setFpError('');
    try {
      const res = await axios.post(`${API}/api/auth/password-reset/request`, { identifier: fpIdentifier });
      setFpChallengeId(res.data.challenge_id);
      setFpMessage(res.data.message);
      setFpResendLeft(res.data.resend_after || 60);
      setFpStep('reset');
    } catch (err) {
      setFpError(err.response?.data?.detail || 'حدث خطأ، حاول مرة أخرى');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResendPasswordReset = async () => {
    if (fpResendLeft > 0) return;
    setFpResendLoading(true);
    setFpError('');
    try {
      const res = await axios.post(`${API}/api/auth/password-reset/resend`, { challenge_id: fpChallengeId });
      setFpResendLeft(res.data.resend_after || 60);
    } catch (err) {
      setFpError(err.response?.data?.detail || 'تعذّر إعادة الإرسال');
    } finally {
      setFpResendLoading(false);
    }
  };

  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    setFpError('');
    if (fpNewPassword.length < 6) {
      setFpError('كلمة المرور يجب ألا تقل عن 6 أحرف');
      return;
    }
    if (fpNewPassword !== fpConfirmPassword) {
      setFpError('كلمتا المرور غير متطابقتين');
      return;
    }
    setFpLoading(true);
    try {
      await axios.post(`${API}/api/auth/password-reset/confirm`, {
        challenge_id: fpChallengeId,
        code: fpCode,
        new_password: fpNewPassword,
      });
      setFpStep('success');
    } catch (err) {
      setFpError(err.response?.data?.detail || 'حدث خطأ، حاول مرة أخرى');
    } finally {
      setFpLoading(false);
    }
  };

  useEffect(() => {
    if (step !== 'otp') return;
    if (expiresLeft <= 0) return;
    const t = setInterval(() => setExpiresLeft((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(t);
  }, [step, expiresLeft]);

  useEffect(() => {
    if (step !== 'otp') return;
    if (resendLeft <= 0) return;
    const t = setInterval(() => setResendLeft((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(t);
  }, [step, resendLeft]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const redirectAfterSuccess = (path, userName) => {
    if (userName) setSuccessUserName(userName);
    setShowSuccessOverlay(true);
    setTimeout(() => { window.location.href = path; }, 950);
  };

  const backToLoginForm = () => {
    setStep('form');
    setOtpCode('');
    setOtpError('');
    setTrustDevice(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('يرجى إدخال الرمز المكوّن من 6 أرقام');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    try {
      const response = await axios.post(`${API}/api/auth/otp/verify`, {
        challenge_id: otp.challengeId,
        code: otpCode,
        trust_device: trustDevice,
      });
      const { access_token, user_info, device_token } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user_info));
      if (device_token) localStorage.setItem(DEVICE_TOKEN_KEY, device_token);
      if (rememberMe) localStorage.setItem('remember_login', 'true');

      const redirectPath = user_info.is_super_admin === true
        ? '/super-admin'
        : (user_info.role === 'coach' && !user_info.club_id ? '/coach' : (user_info.role === 'parent' ? '/parent' : (user_info.club?.account_type === 'venue_owner' ? '/venue-owner-dashboard' : '/dashboard')));
      redirectAfterSuccess(redirectPath, user_info.full_name);
    } catch (err) {
      if (err.response?.status === 400 || err.response?.status === 429) {
        setOtpError(err.response?.data?.detail || 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول من جديد');
        setTimeout(() => backToLoginForm(), 1800);
      } else if (err.response?.status === 401) {
        setOtpError(err.response?.data?.detail || 'رمز غير صحيح');
      } else {
        setOtpError('حدث خطأ، حاول مرة أخرى');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendLeft > 0) return;
    setResendLoading(true);
    setOtpError('');
    try {
      const response = await axios.post(`${API}/api/auth/otp/resend`, { challenge_id: otp.challengeId });
      setExpiresLeft(response.data.expires_in || 300);
      setResendLeft(response.data.resend_after || 60);
      setOtpCode('');
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'تعذّر إعادة الإرسال، حاول لاحقاً');
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'تسجيل الدخول - تسيار';
    const fetchPlatformSettings = async () => {
      try {
        const response = await axios.get(`${API}/api/platform/settings`);
        if (response.data) setPlatformSettings(response.data);
      } catch (err) {
        console.error('خطأ في جلب إعدادات المنصة:', err);
      }
    };
    fetchPlatformSettings();
  }, [API]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.username || !loginData.password) {
      setError('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new URLSearchParams({ username: loginData.username, password: loginData.password });
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      const response = await axios.post(`${API}/api/auth/login`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...(deviceToken ? { 'X-Device-Token': deviceToken } : {}),
        }
      });

      if (response.data?.otp_required) {
        setOtp({
          challengeId: response.data.challenge_id,
          maskedEmail: response.data.masked_email,
          expiresIn: response.data.expires_in,
          resendAfter: response.data.resend_after,
        });
        setExpiresLeft(response.data.expires_in || 300);
        setResendLeft(response.data.resend_after || 60);
        setStep('otp');
        setLoading(false);
        return;
      }

      const { access_token, user_info } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user_info));
      if (rememberMe) localStorage.setItem('remember_login', 'true');

      const redirectPath = user_info.is_super_admin === true
        ? '/super-admin'
        : (user_info.role === 'coach' && !user_info.club_id ? '/coach' : (user_info.role === 'parent' ? '/parent' : (user_info.club?.account_type === 'venue_owner' ? '/venue-owner-dashboard' : '/dashboard')));
      redirectAfterSuccess(redirectPath, user_info.full_name);
    } catch (err) {
      console.error('خطأ في تسجيل الدخول:', err);
      if (err.response?.status === 429) {
        const retryAfter = parseInt(err.response.headers?.['retry-after'], 10);
        const minutes = retryAfter ? Math.ceil(retryAfter / 60) : null;
        setError(err.response?.data?.detail || (minutes ? `محاولات كثيرة — يرجى المحاولة بعد ${minutes} دقيقة` : 'محاولات كثيرة — يرجى المحاولة بعد قليل'));
      } else if (err.response?.status === 401) {
        setError('اسم المستخدم أو البريد الإلكتروني أو كلمة المرور غير صحيحة');
      } else {
        setError('حدث خطأ في تسجيل الدخول. يرجى المحاولة مرة أخرى.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen flex flex-col lg:flex-row" style={{ fontFamily: "'Tajawal', 'Cairo', sans-serif" }} data-testid="login-page">
      <AuthSuccessOverlay show={showSuccessOverlay} title={successUserName ? `أهلاً بك، ${successUserName}` : 'تم تسجيل الدخول بنجاح'} />
      {/* Left Visual Panel (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-[#0A192F]">
        <img src={STADIUM_IMAGE} alt="ملعب رياضي" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/95 via-[#0A192F]/80 to-[#0D9488]/40" />
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{ backgroundImage: 'linear-gradient(to right,#ffffff22 1px,transparent 1px),linear-gradient(to bottom,#ffffff22 1px,transparent 1px)', backgroundSize: '32px 32px' }}
        />
        <div className="relative z-10 flex h-full flex-col p-14 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors w-fit" data-testid="login-back-home-link">
            <span>→</span> العودة للرئيسية
          </Link>

          <div className="mt-10 animate-[fadeUp_0.7s_ease-out]">
            <span className="inline-block rounded-full bg-teal-500/15 border border-teal-400/30 px-4 py-1.5 text-xs font-bold text-teal-300 tracking-wide">
              منصة إدارة رياضية متكاملة
            </span>
            <h2 className="mt-6 font-black text-4xl leading-tight tracking-tight">
              إدارة رياضية ذكية،<br />لنتائج احترافية
            </h2>
            <p className="mt-4 max-w-md text-white/60 text-base leading-relaxed">
              نظّم فرقك، خططك التدريبية، مبارياتك، وشؤونك المالية والإدارية في مكان واحد مبني خصيصاً للأندية والأكاديميات الرياضية.
            </p>
          </div>

          <div className="min-h-[24px] flex-1" />

          <div className="mx-auto w-full max-w-[420px]" data-testid="login-updates-panel-desktop">
            <PlatformUpdatesCarousel />
          </div>

          <div className="mt-8 flex items-center gap-8 text-white/50 text-xs">
            <span>© {new Date().getFullYear()} تسيار</span>
            <span className="h-1 w-1 rounded-full bg-white/30" />
            <span>جميع الحقوق محفوظة</span>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div
        className="flex-1 flex items-center justify-center bg-white relative px-6 sm:px-16 lg:px-24 py-14"
        style={{ backgroundImage: 'linear-gradient(to right,#80808012 1px,transparent 1px),linear-gradient(to bottom,#80808012 1px,transparent 1px)', backgroundSize: '24px 24px' }}
      >
        <div className="lg:hidden absolute top-6 right-6">
          <Link to="/" className="text-sm font-medium text-slate-500 hover:text-teal-600 transition-colors" data-testid="login-back-home-link-mobile">
            → الرئيسية
          </Link>
        </div>

        <div className="w-full max-w-sm">
          <div className="animate-[fadeUp_0.5s_ease-out]">
            {platformSettings.platform_logo ? (
              <img
                src={`${API}/api/files/${platformSettings.platform_logo}`}
                alt={platformSettings.platform_name}
                className="h-20 object-contain"
                data-testid="login-platform-logo"
              />
            ) : (
              <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0D9488] flex items-center justify-center text-2xl font-black text-white">
                {(platformSettings.platform_name || 'تسيار').charAt(0)}
              </div>
            )}
          </div>

          {step === 'form' ? (
            <>
              <div className="mt-8 animate-[fadeUp_0.6s_ease-out]">
                <h1 className="font-black text-3xl tracking-tight text-[#0A192F]">
                  أهلاً بك في {platformSettings.platform_name || 'تسيار'}
                </h1>
                <p className="mt-2 text-slate-500 text-sm">سجّل دخولك لمتابعة عملك الرياضي اليوم</p>
              </div>

              <form onSubmit={handleLogin} className="mt-10 space-y-5" data-testid="login-form">
                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="login-error-message">
                    {error}
                  </div>
                )}

                <div className="animate-[fadeUp_0.7s_ease-out]">
                  <label className="mb-1.5 block text-xs font-bold text-slate-600">اسم المستخدم أو البريد الإلكتروني أو رقم الجوال</label>
                  <input
                    type="text"
                    data-testid="login-username-input"
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    className="h-14 w-full rounded-xl bg-slate-50 px-4 text-slate-800 placeholder-slate-400 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white"
                    placeholder="اسم المستخدم أو البريد الإلكتروني أو رقم الجوال"
                    autoComplete="username"
                    required
                  />
                </div>

                <div className="animate-[fadeUp_0.8s_ease-out]">
                  <label className="mb-1.5 block text-xs font-bold text-slate-600">كلمة المرور</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      data-testid="login-password-input"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="h-14 w-full rounded-xl bg-slate-50 px-4 pl-12 text-slate-800 placeholder-slate-400 outline-none border border-transparent transition-all focus:ring-2 focus:ring-teal-600 focus:bg-white"
                      placeholder="أدخل كلمة المرور"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      data-testid="login-toggle-password-visibility"
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-teal-600 transition-colors"
                    >
                      {showPassword ? 'إخفاء' : 'عرض'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      data-testid="login-remember-me-checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-slate-600 group-hover:text-slate-800 transition-colors">تذكرني</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(true)}
                    data-testid="login-forgot-password-link"
                    className="font-semibold text-slate-500 hover:text-teal-600 hover:underline transition-colors"
                  >
                    نسيت كلمة المرور؟
                  </button>
                </div>

                <button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="relative h-14 w-full overflow-hidden rounded-xl bg-teal-600 text-lg font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_rgba(13,148,136,0.5)] disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none"
                >
                  {loading ? (
                    <span className="relative flex items-center justify-center gap-2">
                      <span className="absolute inset-y-0 w-1/3 bg-white/20" style={{ animation: 'buttonSheenSweep 1.1s ease-in-out infinite' }} />
                      <span className="flex gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                      جارٍ تسجيل الدخول...
                    </span>
                  ) : 'دخول المنصة'}
                </button>
              </form>

              <div className="mt-8 border-t border-slate-100 pt-6 text-center">
                <p className="text-sm text-slate-500">ليس لديك حساب؟</p>
                <Link
                  to="/register"
                  data-testid="login-goto-register-link"
                  className="mt-2 inline-block rounded-xl border-2 border-teal-600 px-6 py-2.5 text-sm font-bold text-teal-600 transition-all hover:bg-teal-600 hover:text-white"
                >
                  سجّل منشأتك أو حسابك كمدرب
                </Link>
              </div>
            </>
          ) : (
            <div data-testid="otp-verification-step">
              <div className="mt-8 animate-[fadeUp_0.4s_ease-out]">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-7 w-7">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </div>
                <h1 className="font-black text-2xl tracking-tight text-[#0A192F]">تحقق من هويتك</h1>
                <p className="mt-2 text-slate-500 text-sm">
                  أرسلنا رمزاً مكوّناً من 6 أرقام إلى <span className="font-bold text-slate-700" data-testid="otp-masked-email">{otp.maskedEmail}</span>
                </p>
              </div>

              <form onSubmit={handleVerifyOtp} className="mt-8 space-y-5">
                {otpError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="otp-error-message">
                    {otpError}
                  </div>
                )}

                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-600 text-center">رمز التحقق</label>
                  <OtpBoxInput length={6} value={otpCode} onChange={setOtpCode} autoFocus testIdPrefix="otp-code" />
                  <p className="mt-2 text-xs text-slate-400 text-center" data-testid="otp-expiry-timer">
                    {expiresLeft > 0 ? `صلاحية الرمز تنتهي بعد ${formatTime(expiresLeft)}` : 'انتهت صلاحية الرمز، اطلب رمزاً جديداً'}
                  </p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer group text-sm">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    data-testid="otp-trust-device-checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-slate-600 group-hover:text-slate-800 transition-colors">تذكّر هذا الجهاز لمدة 30 يوماً</span>
                </label>

                <button
                  type="submit"
                  data-testid="otp-verify-submit-button"
                  disabled={otpLoading || otpCode.length !== 6}
                  className="relative h-14 w-full overflow-hidden rounded-xl bg-teal-600 text-lg font-bold text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_rgba(13,148,136,0.5)] disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-none"
                >
                  {otpLoading ? (
                    <span className="relative flex items-center justify-center gap-2">
                      <span className="absolute inset-y-0 w-1/3 bg-white/20" style={{ animation: 'buttonSheenSweep 1.1s ease-in-out infinite' }} />
                      <span className="flex gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-white animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                      جارٍ التحقق...
                    </span>
                  ) : 'تأكيد الدخول'}
                </button>

                <div className="flex items-center justify-between text-sm pt-2">
                  <button
                    type="button"
                    onClick={backToLoginForm}
                    data-testid="otp-back-to-login-button"
                    className="font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    ← رجوع
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendLeft > 0 || resendLoading}
                    data-testid="otp-resend-button"
                    className="font-semibold text-teal-600 hover:text-teal-700 hover:underline transition-colors disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    {resendLoading ? 'جارٍ الإرسال...' : (resendLeft > 0 ? `إعادة الإرسال بعد ${resendLeft} ثانية` : 'إعادة إرسال الرمز')}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className={`mt-8 overflow-hidden rounded-2xl bg-[#0A192F] p-5 lg:hidden relative ${hasUpdates ? '' : 'hidden'}`} data-testid="login-updates-panel-mobile">
            <img src={STADIUM_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
            <div className="relative">
              <PlatformUpdatesCarousel onData={(d) => setHasUpdates(d.length > 0)} />
            </div>
          </div>
        </div>
      </div>

      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="forgot-password-modal" onClick={closeForgotModal}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl text-right" dir="rtl" onClick={(e) => e.stopPropagation()}>
            {fpStep === 'identifier' && (
              <>
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-7 w-7">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 11-12 0 6 6 0 0112 0zM12 15.75l-1.5 4.5m4.5-4.5l-3-3" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-[#0A192F] text-center">نسيت كلمة المرور؟</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500 text-center">
                  أدخل اسم المستخدم أو البريد الإلكتروني أو رقم الجوال المرتبط بحسابك، وسنرسل رمز تحقق إلى بريدك الموثّق إن وُجد.
                </p>

                {fpError && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-600" data-testid="forgot-password-error">
                    {fpError}
                  </div>
                )}

                <form onSubmit={handleRequestPasswordReset} className="mt-5 space-y-4">
                  <input
                    type="text"
                    required
                    value={fpIdentifier}
                    onChange={(e) => setFpIdentifier(e.target.value)}
                    placeholder="اسم المستخدم أو البريد الإلكتروني أو رقم الجوال"
                    data-testid="forgot-password-identifier-input"
                    className="h-12 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-slate-200 focus:ring-2 focus:ring-teal-600"
                  />
                  <button
                    type="submit"
                    disabled={fpLoading}
                    data-testid="forgot-password-send-button"
                    className="h-12 w-full rounded-xl bg-teal-600 text-sm font-bold text-white disabled:opacity-50"
                  >
                    {fpLoading ? 'جارٍ الإرسال...' : 'إرسال رمز التحقق'}
                  </button>
                </form>

                <button
                  onClick={closeForgotModal}
                  data-testid="forgot-password-modal-close"
                  className="mt-4 w-full text-center text-xs font-semibold text-slate-400 hover:text-slate-600"
                >
                  إغلاق
                </button>
              </>
            )}

            {fpStep === 'reset' && (
              <>
                <h3 className="text-lg font-bold text-[#0A192F] text-center">تحقق وأعد تعيين كلمة المرور</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500 text-center" data-testid="forgot-password-generic-message">
                  {fpMessage}
                </p>

                {fpError && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-600" data-testid="forgot-password-reset-error">
                    {fpError}
                  </div>
                )}

                <form onSubmit={handleConfirmPasswordReset} className="mt-5 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-bold text-slate-600 text-center">رمز التحقق</label>
                    <OtpBoxInput length={6} value={fpCode} onChange={setFpCode} autoFocus testIdPrefix="fp-code" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold text-slate-600">كلمة المرور الجديدة</label>
                    <input
                      type="password"
                      required
                      value={fpNewPassword}
                      onChange={(e) => setFpNewPassword(e.target.value)}
                      placeholder="6 أحرف على الأقل"
                      data-testid="forgot-password-new-password-input"
                      className="h-12 w-full rounded-xl bg-slate-50 px-4 outline-none border border-slate-200 focus:ring-2 focus:ring-teal-600"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-bold text-slate-600">تأكيد كلمة المرور</label>
                    <input
                      type="password"
                      required
                      value={fpConfirmPassword}
                      onChange={(e) => setFpConfirmPassword(e.target.value)}
                      placeholder="أعد كتابة كلمة المرور"
                      data-testid="forgot-password-confirm-password-input"
                      className="h-12 w-full rounded-xl bg-slate-50 px-4 outline-none border border-slate-200 focus:ring-2 focus:ring-teal-600"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={fpLoading || fpCode.length !== 6}
                    data-testid="forgot-password-confirm-button"
                    className="h-12 w-full rounded-xl bg-teal-600 text-sm font-bold text-white disabled:opacity-50"
                  >
                    {fpLoading ? 'جارٍ التأكيد...' : 'تعيين كلمة المرور'}
                  </button>

                  <button
                    type="button"
                    onClick={handleResendPasswordReset}
                    disabled={fpResendLeft > 0 || fpResendLoading}
                    data-testid="forgot-password-resend-button"
                    className="w-full text-center text-xs font-semibold text-teal-600 hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    {fpResendLoading ? 'جارٍ الإرسال...' : (fpResendLeft > 0 ? `إعادة الإرسال بعد ${fpResendLeft} ثانية` : 'إعادة إرسال الرمز')}
                  </button>
                </form>

                <button
                  onClick={closeForgotModal}
                  data-testid="forgot-password-modal-close"
                  className="mt-4 w-full text-center text-xs font-semibold text-slate-400 hover:text-slate-600"
                >
                  إغلاق
                </button>
              </>
            )}

            {fpStep === 'success' && (
              <div className="text-center" data-testid="forgot-password-success">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-7 w-7">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-[#0A192F]">تم تعيين كلمة المرور بنجاح</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة. تم إلغاء كل الجلسات والأجهزة الموثوقة السابقة لهذا الحساب.
                </p>
                <button
                  onClick={closeForgotModal}
                  data-testid="forgot-password-success-close-button"
                  className="mt-6 h-11 w-full rounded-xl bg-teal-600 text-sm font-bold text-white hover:bg-teal-700 transition-colors"
                >
                  تسجيل الدخول
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default EnhancedProfessionalLogin;
